import { useEffect, useCallback, useRef } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number; // 하단에서 몇 픽셀 전에 로드할지
}

export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 100
}: UseInfiniteScrollOptions) => {
  const isLoadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);

  // ref 업데이트
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const handleScroll = useCallback(() => {
    // 로딩 중이거나 더 이상 데이터가 없으면 리턴
    if (isLoadingRef.current || !hasMoreRef.current) {
      return;
    }

    // 스크롤 위치 계산
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // 하단에 가까워지면 데이터 로드
    if (scrollTop + windowHeight + threshold >= documentHeight) {
      onLoadMore();
    }
  }, [onLoadMore, threshold]);

  useEffect(() => {
    // 스크롤 이벤트 리스너 등록
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      // 클린업
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // 초기 로드 시 화면이 비어있으면 자동으로 더 로드
  useEffect(() => {
    const timer = setTimeout(() => {
      if (hasMore && !isLoading && document.documentElement.scrollHeight <= window.innerHeight) {
        onLoadMore();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [hasMore, isLoading, onLoadMore]);
};
